import { Enums, Constants } from '@bode-canada/core';
import NumberFormat, { CurrencyCode } from '@/ui-kit/helpers/NumberFormatHelper';
import { IconType } from '@/ui-kit/components/icon/enums';
import NumberHelper from '@/utils/NumberHelper';
import { getDiff } from '@/utils/date';
import { PropertyOverviewTitle } from '../enums';
import { UNAVAILABLE_MESSAGE } from '../../constants';

export default class ListingOverviewHelper {
  static getKeyDetails(transaction, isBode, lastSoldPrice) {
    return [
      {
        title: 'Date Listed',
        icon: IconType.CalendarEmpty,
        value: transaction.publishedDate,
      },
      {
        title: 'Date Sold',
        icon: IconType.Calendar,
        value: transaction.soldDate,
      },
      {
        title: 'Days on Market',
        icon: IconType.Clock,
        value: transaction.daysOnMarket,
      },
      {
        title: 'List Price',
        icon: IconType.HomePrice,
        value: transaction.originalListPrice,
      },
      {
        title: 'Sale Price',
        icon: IconType.HomeBode,
        value: transaction.soldPrice,
        appendIcon: lastSoldPrice && transaction.isSold
          && lastSoldPrice !== transaction.price
          && (lastSoldPrice > transaction.price ? IconType.ArrowUp : IconType.ArrowDown),
      },
      {
        title: 'Sold / List Ratio',
        icon: IconType.PieChart,
        value: transaction.priceRatio,
      },
      transaction.sellerSavings
        ? {
          title: 'Seller Savings',
          icon: IconType.Coins,
          value: isBode && transaction.sellerSavings,
        } : {},
    ].filter(this.#doesValueExist);
  }

  static getListingPropertyOverviewCharacteristics(listing, excludedCharacteristics = []) {
    return [
      {
        title: PropertyOverviewTitle.HomeType,
        icon: IconType.Homes,
        value: this.#getBuildingType(listing.buildingClass),
      },
      {
        title: PropertyOverviewTitle.GarageSize,
        icon: IconType.GarageSize,
        value: listing.garageSqft && `${listing.garageSqft} sqft`,
      },
      {
        title: PropertyOverviewTitle.BuildingType,
        icon: IconType.Building,
        value: this.#getBuildingType(listing.buildingType),
      },
      {
        title: PropertyOverviewTitle.LotSize,
        icon: IconType.HomeSize,
        value: this.#formatLotSize(listing.lotSizeSqft),
      },
      {
        title: PropertyOverviewTitle.Community,
        icon: IconType.Community,
        value: listing.community,
      },
      {
        title: PropertyOverviewTitle.Beds,
        icon: IconType.Bed,
        value: listing.totalBeds,
      },
      {
        title: PropertyOverviewTitle.Heating,
        icon: IconType.Heating,
        value: this.#stringifyArray(listing.heatingFuel),
      },
      {
        title: PropertyOverviewTitle.FullBaths,
        icon: IconType.Bath,
        value: listing.fullBaths,
      },
      {
        title: PropertyOverviewTitle.Cooling,
        icon: IconType.AcUnit,
        value: this.#stringifyArray(listing.cooling),
      },
      {
        title: PropertyOverviewTitle.HalfBaths,
        icon: IconType.Bathroom,
        value: listing.halfBaths,
      },
      {
        title: PropertyOverviewTitle.ParkingSpace,
        icon: IconType.Cars,
        value: listing.totalParking,
      },
      {
        title: PropertyOverviewTitle.YearBuilt,
        icon: IconType.CalendarEmpty,
        value: listing.yearBuilt,
      },
      {
        title: PropertyOverviewTitle.PropertyTaxes,
        icon: IconType.Zoning,
        value: this.#formatMoney(listing.taxes),
      },
      {
        title: PropertyOverviewTitle.HOA,
        icon: IconType.Hoa,
        value: this.#formatMoney(
          listing.hoaFeeAmount,
          ` / ${listing.hoaFeeSchedule === Enums.HOAFeeSchedule.ANNUAL ? 'year' : 'month'}`,
        ),
      },
      {
        title: this.#getTimeOnlineTitle(listing),
        icon: IconType.Clock,
        value: this.#getTimeOnline(listing.daysOnMarket, listing.publishedAt),
      },
      {
        title: PropertyOverviewTitle.MLSNumber,
        icon: IconType.Realtor,
        value: listing.externalId,
      },
      {
        title: PropertyOverviewTitle.BodeID,
        icon: IconType.BodePin,
        value: listing.isBode && listing.listingId,
      },
      {
        title: PropertyOverviewTitle.SqftPrice,
        icon: IconType.HomePrice,
        value: listing.finishedSqft && this.#formatMoney(listing.price / listing.finishedSqft),
      },
      {
        title: PropertyOverviewTitle.LandUse,
        icon: IconType.Landscape,
        value: listing.landUse,
      },
      {
        title: PropertyOverviewTitle.Style,
        icon: IconType.HomeBush,
        value: listing.style,
      },
    ]
      .filter(this.#doesValueExist)
      .filter(({ title }) => !excludedCharacteristics.includes(title));
  }

  static #formatLotSize(lotSize) {
    const lostSizeInAcres = NumberHelper.sqftToAcres(lotSize);

    return lotSize && (lostSizeInAcres < 1 ? `${lotSize} Sqft` : `${lostSizeInAcres} Acres`);
  }

  static #getTimeOnlineTitle(listing) {
    if (listing.isBode) {
      return PropertyOverviewTitle.TimeOnBode;
    }
    if (listing.isRAE) {
      return PropertyOverviewTitle.DaysOnPlatform;
    }
    return PropertyOverviewTitle.DaysOnMarket;
  }

  static #getTimeOnline(daysOnMarket, publishedAt) {
    const daysOnline = getDiff({ toDatetime: publishedAt, unit: 'day' });

    return daysOnMarket || (publishedAt && daysOnline);
  }

  static #stringifyArray(input) {
    if (this.#isString(input)) {
      return input;
    }

    if (this.#isArray(input) && !!input.length) {
      return input?.join(', ');
    }

    return UNAVAILABLE_MESSAGE;
  }

  static #isString(input) {
    return typeof input === 'string';
  }

  static #isArray(input) {
    return Array.isArray(input);
  }

  static #getBuildingType(buildingType) {
    return buildingType === Constants.DEFAULT_BUILDING_TYPE ? UNAVAILABLE_MESSAGE : buildingType;
  }

  static #formatMoney(input, postfix = '') {
    return input && `${NumberFormat.formatCurrency(
      input,
      CurrencyCode.CanadianDollars,
    )}${postfix}`;
  }

  static #doesValueExist({ value }) {
    return !!value && (value.length || value.toString());
  }
}
